/**
 * The Module / Chapter / Topic columns, in one place.
 *
 * These three columns are one hierarchy spread across three cells: exactly one
 * holds the row's own name, and the others show its parents, recessive, so the
 * repetition down the table reads as grouping. Two surfaces render them — the
 * Modules & Topics table and a batch's Curriculum plan — over completely
 * different row types (canonical modules vs coverage topics), so what is shared
 * is the column definition, not the data model.
 *
 * Generic over the row: a consumer supplies accessors and gets the same
 * columns, the same emphasis rules and the same mobile collapse.
 */
import type { ReactNode } from 'react';
import type { ColumnDef } from '../components';

export type HierarchyLevel = 'module' | 'chapter' | 'topic';

export interface HierarchyAccessors<T> {
  level: (row: T) => HierarchyLevel;
  moduleName: (row: T) => string;
  chapterName: (row: T) => string;
  topicName: (row: T) => string;
}

const muted = (v: string): ReactNode =>
  v ? <span className="dc-muted">{v}</span> : <span className="dc-no-value">—</span>;

const own = (v: string): ReactNode => <span className="dc-name-link">{v}</span>;

/**
 * Build the three columns.
 *
 * On mobile a card has a single heading, so the Module column supplies the
 * row's OWN name whatever its level, and the other two collapse into a
 * parent-path meta line rather than repeating as empty dashes.
 */
export function hierarchyColumns<T>(a: HierarchyAccessors<T>): ColumnDef<T>[] {
  return [
    {
      key: 'module',
      header: 'Module',
      mobile: 'title',
      render: (r) => (a.level(r) === 'module' ? own(a.moduleName(r)) : muted(a.moduleName(r))),
      mobileRender: (r) => {
        const lvl = a.level(r);
        return lvl === 'module' ? a.moduleName(r) : lvl === 'chapter' ? a.chapterName(r) : a.topicName(r);
      },
    },
    {
      key: 'chapter',
      header: 'Chapter',
      mobileLabel: 'In',
      render: (r) => {
        const lvl = a.level(r);
        if (lvl === 'chapter') return own(a.chapterName(r));
        if (lvl === 'topic') return muted(a.chapterName(r));
        return <span className="dc-no-value">—</span>;
      },
      mobileRender: (r) => {
        const lvl = a.level(r);
        if (lvl === 'module') return '';
        if (lvl === 'chapter') return a.moduleName(r);
        const parents = [a.moduleName(r), a.chapterName(r)].filter(Boolean);
        return parents.join(' › ');
      },
    },
    {
      key: 'topic',
      header: 'Topic',
      mobile: 'hide',
      render: (r) => (a.level(r) === 'topic' ? own(a.topicName(r)) : <span className="dc-no-value">—</span>),
    },
  ];
}
